import { Link } from 'react-router-dom';
import { destinationPath, EDITABLE_ITEM_KEYS } from '../destinationPath';
import type { ReadinessLoad } from '../useEmployeeReadiness';
import {
  UNSUPPORTED_BLOCKER_LABEL,
  type EmployeeSetupReadiness,
  type ReadinessItem,
  type ReadinessStatus,
} from '../types';
import { ReadinessStatusChip } from './ReadinessStatusChip';
import '../employee-readiness.css';

function stateText(item: ReadinessItem): string {
  switch (item.state) {
    case 'Complete': return 'Complete';
    case 'Missing': return 'Missing';
    case 'NotApplicable': return 'Not applicable';
    case 'Unverifiable': return 'Could not be verified';
    case 'CapabilityUnavailable': return item.severity === 'Blocker' ? UNSUPPORTED_BLOCKER_LABEL : 'Not yet supported.';
  }
}

function headline(readiness: EmployeeSetupReadiness): string {
  const open = readiness.items.filter(
    (i) => i.severity === 'Blocker' && (i.state === 'Missing' || i.state === 'CapabilityUnavailable'),
  ).length;
  const status: ReadinessStatus = readiness.status;
  switch (status) {
    case 'Ready': return 'All payroll requirements are met.';
    case 'ReadyWithWarnings': return 'Payroll can proceed, but some items need review first.';
    case 'NotApplicable': return 'Payroll readiness does not apply to this employee.';
    case 'NeedsReview':
      return 'Some requirements could not be verified right now, so readiness cannot be confirmed. Try again shortly.';
    case 'Blocked': {
      const unsupported = readiness.progress.unsupportedCount;
      const base = `${open} ${open === 1 ? 'requirement blocks' : 'requirements block'} live payroll for this employee.`;
      return unsupported > 0
        ? `${base} ${unsupported} of them can’t be completed in EPN yet, so this employee stays blocked until those are available.`
        : base;
    }
  }
}

function ItemRow({ employeeId, item }: { employeeId: string; item: ReadinessItem }) {
  const path = destinationPath(employeeId, item);
  const editable = EDITABLE_ITEM_KEYS.has(item.key) && item.state !== 'Complete';
  return (
    <li className={`readiness-item readiness-item--${item.state}`} data-item-key={item.key}>
      <div className="readiness-item-main">
        <strong>{item.label}</strong>
        {item.severity !== 'Blocker' && <span className="readiness-severity">{item.severity === 'Warning' ? 'Warning' : 'Info'}</span>}
        <span className="readiness-item-state">{stateText(item)}</span>
        <p className="muted readiness-item-message">{item.message}</p>
      </div>
      {path && (
        <Link className="link readiness-item-link" to={path} aria-label={`${editable ? 'Edit' : 'View section for'} ${item.label}`}>
          {editable ? 'Edit' : 'View section'}
        </Link>
      )}
    </li>
  );
}

function Group({ title, note, employeeId, items }: { title: string; note?: string; employeeId: string; items: ReadinessItem[] }) {
  if (items.length === 0) return null;
  return (
    <section className="readiness-group" aria-label={title}>
      <h4 className="readiness-group-title">{title} <span className="muted">({items.length})</span></h4>
      {note && <p className="muted readiness-group-note">{note}</p>}
      <ul className="readiness-list">
        {items.map((item) => <ItemRow key={item.key} employeeId={employeeId} item={item} />)}
      </ul>
    </section>
  );
}

export interface SetupSummaryPanelProps {
  employeeId: string;
  load: ReadinessLoad;
  onRetry: () => void;
}

/** The Setup Summary: overall server-computed readiness, setup progress shown separately, and every
 * requirement grouped by what the Admin can do about it. Renders only what the server returned. */
export function SetupSummaryPanel({ employeeId, load, onRetry }: SetupSummaryPanelProps) {
  if (load.kind === 'loading') {
    return <p className="muted" role="status">Checking payroll readiness…</p>;
  }

  if (load.kind === 'forbidden') {
    return <p className="error" role="alert">You don’t have permission to view payroll readiness for this employee.</p>;
  }

  if (load.kind === 'notFound') {
    return <p className="error" role="alert">This employee could not be found.</p>;
  }

  if (load.kind === 'error') {
    return (
      <div role="alert">
        <p className="error">{load.message}</p>
        <button type="button" onClick={onRetry}>Try again</button>
      </div>
    );
  }

  const readiness = load.data;
  if (readiness.items.length === 0) {
    return <p className="muted">No readiness checks were returned for this employee.</p>;
  }

  const relevant = readiness.items.filter((i) => i.severity !== 'Informational');
  const actionNeeded = relevant.filter((i) => i.state === 'Missing' || i.state === 'Unverifiable');
  const unsupported = relevant.filter((i) => i.state === 'CapabilityUnavailable');
  const complete = relevant.filter((i) => i.state === 'Complete' || i.state === 'NotApplicable');
  const informational = readiness.items.filter((i) => i.severity === 'Informational');
  const { progress } = readiness;

  return (
    <div className="readiness-summary">
      <div className="readiness-overview">
        <div className="readiness-overview-status">
          <span className="muted">Payroll readiness</span>
          <ReadinessStatusChip value={readiness.status} />
        </div>
        <p className="readiness-headline">{headline(readiness)}</p>
        {readiness.status === 'NeedsReview' && <button type="button" onClick={onRetry}>Try again</button>}
      </div>

      <div className="readiness-progress">
        <div
          role="progressbar"
          aria-label="Setup progress"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={progress.percent}
          className="readiness-progress-track"
        >
          <div className="readiness-progress-fill" style={{ width: `${progress.percent}%` }} />
        </div>
        <p className="muted">
          Setup progress: {progress.completedCount} of {progress.applicableCount} supported items complete ({progress.percent}%).
          {progress.unsupportedCount > 0 && ` ${progress.unsupportedCount} requirements aren’t available in EPN yet and aren’t counted.`}
          {progress.unverifiableCount > 0 && ` ${progress.unverifiableCount} couldn’t be verified and aren’t counted.`}
        </p>
        <p className="muted">Progress is not the same as readiness: this employee can reach 100% progress and still be blocked.</p>
      </div>

      <Group title="Action needed" employeeId={employeeId} items={actionNeeded} />
      <Group
        title="Not yet supported"
        note={
          unsupported.some((i) => i.severity === 'Blocker')
            ? 'The SRS requires these for payroll, but EPN can’t capture them yet. Until it can, they keep this employee blocked for live payroll.'
            : 'EPN can’t capture these yet. They don’t block payroll on their own.'
        }
        employeeId={employeeId}
        items={unsupported}
      />
      <Group title="Completed" employeeId={employeeId} items={complete} />
      <Group title="For your information" employeeId={employeeId} items={informational} />

      <p className="muted readiness-footnote">
        Checked {new Date(readiness.checkedAtUtc).toLocaleString()} as of {readiness.asOfDate} · rules {readiness.rulesVersion}
      </p>
    </div>
  );
}
